import { formatAcfrCategory } from '~support-at-home/lib/acfrCategory';
import { deriveOriginLink } from '~support-at-home/lib/derive';

import { faEdit } from '~components/icon';
import { Column, ColumnPickerGroup } from '~components/table-view';
import { linkCellRender } from '~components/table-view/columnRenderers';

import { ExportColumn, ExportColumnType } from '~support-at-home/models/exportColumns';

import { ColumnSpec } from '~types/columnSpec';

const RIGHT_ALIGN = { header: 'cell-align-right', column: 'cell-align-right' };

// Desired display order for this table. This is purely a frontend presentation concern: the
// backend's specs array is grouped by kind (base/deep_linked/dynamic), which doesn't match the
// order users expect to read this table in. Any spec key not listed here is appended at the end
// rather than dropped, so a newly-registered column still shows up without a frontend change.
const COLUMN_ORDER = [
  'claim_item_status',
  'reference',
  'claim_reference',
  'claims_month',
  'recon_reference',
  'statement_reference',
  'statement_month',
  'service_date',
  'client_name',
  'client_naps_service_id',
  'client_care_recipient_id',
  'client_care_manager_name',
  'sah_service_type',
  'sah_category',
  'tp_service_type',
  'acfr_category',
  'qty',
  'total',
  'invoice_amount',
  'invoice_number',
  'funding_type',
  'package_name',
  'package_id',
];

// Column picker grouping — a purely presentational concern, same as COLUMN_ORDER above. A spec
// key not listed in any group falls into a trailing "Other" group in the picker (handled by
// ColumnPickerModal itself), rather than being dropped.
export const COLUMN_GROUPS: ColumnPickerGroup[] = [
  { title: 'Core', keys: ['id', 'reference', 'service_date', 'qty'] },
  { title: 'Claim', keys: ['claim_reference', 'claim_item_status', 'claim_item_status_tag', 'claim_id', 'claims_month'] },
  {
    title: 'Client',
    keys: ['client_name', 'client_naps_service_id', 'client_care_recipient_id', 'client_care_manager_name'],
  },
  { title: 'Service', keys: ['sah_service_type', 'sah_category', 'tp_service_type'] },
  { title: 'Financial', keys: ['total', 'invoice_amount', 'invoice_number', 'funding_type', 'acfr_category'] },
  { title: 'Reconciliation', keys: ['recon_reference', 'statement_reference', 'statement_month'] },
  { title: 'Package', keys: ['package_name', 'package_id'] },
  { title: 'Origin (metadata)', keys: ['origin_tag', 'origin_appointment_id', 'origin_supplier_invoice_item_id'] },
];

/**
 * Per-column rendering behaviour that can't come from the backend's specs contract — it's
 * genuine UI code (navigation callbacks, cell formatting). Keyed by column key; a spec key with
 * no entry here renders as plain read-only text. Only `id`/`header`/`showColumn` are spec-driven.
 */
const columnBehaviour = (navigateToClaim: (id: string) => void): Record<string, Partial<Column>> => ({
  claim_reference: {
    renderReadOnly: linkCellRender((row) => navigateToClaim(String(row.claim_id)), faEdit),
  },
  service_date: { type: 'date' },
  claims_month: { type: 'date' },
  statement_month: { type: 'date' },
  acfr_category: { deriveValue: (row) => formatAcfrCategory(row.acfr_category) },
  client_naps_service_id: { classNames: RIGHT_ALIGN },
  qty: { type: 'number', classNames: RIGHT_ALIGN },
  total: { type: 'currency', classNames: RIGHT_ALIGN },
  invoice_amount: {
    type: 'currency',
    deriveValue: (row) => (row.origin_tag === 'invoice' ? row.invoice_amount : null),
    classNames: RIGHT_ALIGN,
  },
});

// origin_link has no entry in the specs contract at all — a client-only derived affordance, not
// real data — so it always renders, unaffected by the column picker.
const originLinkColumn = (): Column => ({
  id: 'origin_link',
  header: 'Origin Link',
  type: 'readonly',
  deriveValue: (row) => (deriveOriginLink(row) ? row.origin_tag : null),
  renderReadOnly: linkCellRender((row) => {
    const link = deriveOriginLink(row);
    if (link) window.open(link, '_blank', 'noopener,noreferrer');
  }, faEdit),
});

const orderColumnSpecs = (columnSpecs: ColumnSpec[]): ColumnSpec[] => {
  const specsByKey = new Map(columnSpecs.map((spec) => [spec.key, spec]));
  const ordered = COLUMN_ORDER.map((key) => specsByKey.get(key)).filter((spec): spec is ColumnSpec => Boolean(spec));
  const remaining = columnSpecs.filter((spec) => !COLUMN_ORDER.includes(spec.key));
  return [...ordered, ...remaining];
};

export const columnsFactory = (columnSpecs: ColumnSpec[], navigateToClaim: (id: string) => void): Column[] => {
  const behaviour = columnBehaviour(navigateToClaim);

  const specColumns = orderColumnSpecs(columnSpecs).map(
    (spec): Column => ({
      id: spec.key,
      header: spec.label,
      type: 'readonly',
      showColumn: spec.visible,
      ...behaviour[spec.key],
    }),
  );

  return [...specColumns, originLinkColumn()];
};

// ── CSV export column contract (TAV-3276 open question: "does the CSV export follow the
// visible columns?" mostly, with two declared exceptions, see ALWAYS_EXPORTED /
// NEVER_EXPORTED below) ──────────────────────────────────────────────────────────────
//
// The document-engine's CSV generator has no knowledge of this table's columns at all: it
// renders whatever `{ key, label, type }` list is sent along with the export job, in that
// order. This derives that list from the same `columns` the on-screen table is currently
// rendering, so a CSV export tracks what's visible on screen for most columns: hiding or
// showing a column updates the next export with no code change here needed. The declared
// exceptions below exist because on-screen visibility and export inclusion are genuinely
// different questions for a couple of columns.

// Columns whose CSV export type doesn't just follow their on-screen table `type` — acfr_category
// renders through `deriveValue` (formatAcfrCategory) rather than its table `type` (plain
// 'readonly'), so the CSV needs the same explicit instruction to apply that label mapping.
export const EXPORT_TYPE_OVERRIDES: Partial<Record<string, ExportColumnType>> = {
  acfr_category: 'acfr_category',
};

const toExportType = (column: Column): ExportColumnType => {
  const override = EXPORT_TYPE_OVERRIDES[column.id];
  if (override) return override;
  if (column.type === 'currency' || column.type === 'date' || column.type === 'number') return column.type;
  return 'text';
};

// toExportType() reads column.type and EXPORT_TYPE_OVERRIDES, never deriveValue, so any
// columnBehaviour entry that carries a deriveValue but has no EXPORT_TYPE_OVERRIDES entry
// exports the row's raw value while the screen shows the derived one. That's only safe when
// the two are guaranteed identical. DERIVED_VALUE_MATCHES_RAW is that guarantee, made explicit
// so it must be consciously edited rather than silently assumed: see the registry-completeness
// test in claimItemsDashboardList.test.ts, which fails the build if a new deriveValue is added
// without either an EXPORT_TYPE_OVERRIDES entry or an allowlist entry here.
export const DERIVED_VALUE_MATCHES_RAW = new Set([
  // invoice_amount's deriveValue returns row.invoice_amount only when row.origin_tag ===
  // 'invoice'. tp-assist-api's company_sah_claim_items.mjs applies the exact same gate before
  // the value ever leaves the server (its invoice_amount VIRTUAL getter computes
  // isInvoiceOrigin = getDataValue('origin_tag') === 'invoice' and returns null otherwise), so
  // the raw row value the export reads is already identical to what deriveValue would produce.
  'invoice_amount',
]);

// Enumerates the columnBehaviour keys that carry a deriveValue, for the registry-completeness
// test to check against EXPORT_TYPE_OVERRIDES / DERIVED_VALUE_MATCHES_RAW. The navigateToClaim
// callback only affects claim_reference's renderReadOnly, not any deriveValue, so a no-op is
// fine here.
export const derivedValueColumnKeys = (): string[] =>
  Object.entries(columnBehaviour(() => undefined))
    .filter(([, behaviour]) => Boolean(behaviour.deriveValue))
    .map(([key]) => key);

// The export column list is a DECLARED contract, not one derived from on-screen visibility
// (`column.showColumn`, which comes from the specs contract's resolved `visible`). The two
// questions look similar but are not the same question: `defaultVisibleInTables` (and the
// per-column `hideable` override tp-assist-api applies on top of it) answers "should this
// column be on by default in the column picker", a screen concern. It cannot also answer
// "does this column belong in the customer-facing CSV", an export concern. Two columns
// answer those two questions differently:
//   - origin_tag defaults to hidden on screen (`defaultVisibleInTables: false`), but it is the
//     discriminator the invoice_amount deriveValue (and tp-assist-api's matching VIRTUAL
//     getter) gates on, so it is what lets someone auditing the export see why a row's
//     Inv Total is blank. It belongs in the export even when hidden on screen.
//   - id is forced visible on screen (`hideableInTables: false`, so resolveColumnVisibility
//     always returns visible: true), but it is an internal UUID with no place in a finance
//     export. It does not belong in the export even though it is always shown on screen.
// ALWAYS_EXPORTED and NEVER_EXPORTED make both cases explicit instead of emergent.

// Included in the export regardless of column.showColumn. Not in COLUMN_ORDER, so origin_tag
// naturally sorts to the end of `columns`, matching its position as the final column in the
// document-engine's previous hardcoded header list. Do not add it to COLUMN_ORDER.
const ALWAYS_EXPORTED = new Set(['origin_tag']);

// Excluded from the export regardless of column.showColumn. NEVER_EXPORTED takes precedence
// over ALWAYS_EXPORTED. origin_link is a client-only derived affordance with no backing
// dashboard data, not something a server-generated CSV can produce. id is an internal UUID
// that has no place in a customer-facing finance export.
const NEVER_EXPORTED = new Set(['origin_link', 'id']);

export const exportColumnsFor = (columns: Column[]): ExportColumn[] =>
  columns
    .filter(
      (column) => !NEVER_EXPORTED.has(column.id) && (ALWAYS_EXPORTED.has(column.id) || column.showColumn !== false),
    )
    .map((column) => ({
      key: column.id,
      label: typeof column.header === 'string' ? column.header : column.id,
      type: toExportType(column),
    }));
